/**
 * Convert a string to a URL-safe slug
 * Removes special characters, replaces spaces with hyphens, and converts to lowercase
 *
 * @param {string} text - The text to convert to a slug
 * @returns {string} URL-safe slug
 *
 * @example
 * slugify('AI Tools') // 'ai-tools'
 * slugify('My Group Name!') // 'my-group-name'
 * slugify('Test & Development') // 'test-development'
 */
export function slugify(text) {
  if (!text) return ''

  return (
    text
      .toString()
      .toLowerCase()
      .trim()
      // Remove apostrophes
      .replace(/'/g, '')
      // Replace spaces with hyphens
      .replace(/\s+/g, '-')
      // Remove all non-word chars (except hyphens)
      .replace(/[^\w-]+/g, '')
      // Replace multiple hyphens with single hyphen
      .replace(/--+/g, '-')
      // Remove leading/trailing hyphens
      .replace(/^-+/, '')
      .replace(/-+$/, '')
  )
}

/**
 * Generate a unique slug by appending a number if the slug already exists
 *
 * @param {string} baseSlug - The base slug to make unique
 * @param {Array} existingSlugs - Array of existing slugs to check against
 * @returns {string} Unique slug
 *
 * @example
 * makeUniqueSlug('ai-tools', ['ai-tools']) // 'ai-tools-1'
 * makeUniqueSlug('ai-tools', ['ai-tools', 'ai-tools-1']) // 'ai-tools-2'
 */
export function makeUniqueSlug(baseSlug, existingSlugs = []) {
  if (!existingSlugs.includes(baseSlug)) {
    return baseSlug
  }

  let counter = 1
  let uniqueSlug = `${baseSlug}-${counter}`

  while (existingSlugs.includes(uniqueSlug)) {
    counter++
    uniqueSlug = `${baseSlug}-${counter}`
  }

  return uniqueSlug
}
